import { useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";

// Parse a handle (last path segment) from a profile URL.
export const handleFromUrl = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    const seg = u.pathname.split("/").filter(Boolean)[0] || "";
    return seg.replace(/^@/, "");
  } catch {
    return url.replace(/^@/, "");
  }
};

// Facebook Page Plugin — renders a public Page's live timeline inside an iframe (no API key).
export function FacebookPageEmbed({ url, testid }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(500);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) {
        const w = Math.max(180, Math.min(500, wrapRef.current.offsetWidth));
        setWidth(w);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const src = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(url)}&tabs=timeline&width=${width}&height=720&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true`;

  return (
    <div ref={wrapRef} className="w-full flex justify-center" data-testid={testid}>
      {failed ? (
        <OpenExternal url={url} label="Open Facebook Page" />
      ) : (
        <iframe title="Facebook timeline" src={src} width={width} height={720}
          style={{ border: "none", overflow: "hidden", borderRadius: 12, background: "#121620" }}
          scrolling="no" frameBorder="0" allowFullScreen={true}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          onError={() => setFailed(true)} />
      )}
    </div>
  );
}

// X / Twitter timeline widget — renders the public profile timeline inside the app.
export function TwitterTimelineEmbed({ handle, profileUrl, testid, onOpen }) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.twttr?.widgets || !ref.current) return;
      window.twttr.widgets.load(ref.current).then(() => !cancelled && setLoaded(true));
    };
    if (window.twttr?.widgets) {
      render();
    } else {
      let s = document.getElementById("twitter-wjs");
      if (!s) {
        s = document.createElement("script");
        s.id = "twitter-wjs";
        s.src = "https://platform.twitter.com/widgets.js";
        s.async = true;
        document.body.appendChild(s);
      }
      s.addEventListener("load", render);
    }
    const t = setTimeout(() => !cancelled && setLoaded((v) => v), 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [handle]);

  return (
    <div data-testid={testid}>
      <div ref={ref} className="min-h-[200px]">
        <a className="twitter-timeline" data-theme="dark" data-height="720" data-chrome="noheader nofooter transparent"
          href={`https://twitter.com/${handle}`}>Posts by @{handle}</a>
      </div>
      <div className="mt-3 text-center">
        <OpenExternal url={profileUrl || `https://twitter.com/${handle}`} label="Open on X" small onClick={onOpen} />
      </div>
    </div>
  );
}

export function OpenExternal({ url, label, note, small, onClick }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="open-external-link" onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full bg-[#E11D2A] text-white font-semibold hover:bg-[#c41824] transition-colors ${small ? "h-9 px-5 text-sm" : "h-12 px-7"}`}>
      {label} <ExternalLink className={small ? "w-4 h-4" : "w-5 h-5"} />
    </a>
  );
}

// For platforms that block full-profile embedding (Instagram, TikTok): a clean in-app panel.
export function LinkOnlyPanel({ platform, url, color, Icon, note, onOpen }) {
  return (
    <div className="rounded-2xl bg-[#121620] border border-[#273042] p-8 text-center" data-testid={`linkpanel-${platform}`}>
      <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${color}1a` }}>
        <Icon className="w-8 h-8" style={{ color }} />
      </div>
      <p className="text-white font-bold text-lg">{platform} content</p>
      <p className="text-slate-400 text-sm mt-1 max-w-sm mx-auto">{note}</p>
      <div className="mt-5"><OpenExternal url={url} label={`Open ${platform}`} onClick={onOpen} /></div>
    </div>
  );
}

export function EmbedLoading() {
  return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>;
}
